import React, { FC, useState } from 'react'

import { OfferStatus, OfferUpdateVariables } from '../../services'
import { useAuth, useBasket, useToastify } from '../../providers'
import {
  DeclineOfferModal,
  DeclineOfferModalProps,
  DeleteOfferModal,
  DeleteOfferModalProps
} from '../../views'

import { ResponsiveMediaView } from '../responsive-media-view'
import { Column } from '../column'
import { MenuSlider } from '../menu-slider'
import { getCurrencyByProduct, zeroPad } from '../helpers'

import { useStyle } from './offers-list-item.styles'
import { OfferListItemProps } from './offers-list-item.types'
import {
  OfferListItemHeader,
  OfferListItemHeaderMobile
} from './offers-list-item-header'
import { OfferListItemLine } from './offers-list-item-line'
import { OfferListItemVariantLine } from './offers-list-item-variant-line'

export const OfferListItem: FC<OfferListItemProps> = ({
  id,
  offer,
  onChangeStatus,
  onContactVendor
}) => {
  const classes = useStyle()
  const { open } = useToastify()
  const { onAddOfferId, onRemoveOfferId } = useBasket()
  const { onUpdateOfferTotalCount } = useAuth()
  const [openDecline, changeOpenDecline] = useState(false)
  const [openDelete, changeOpenDelete] = useState(false)

  const { product } = offer
  const currency = getCurrencyByProduct(product)

  const isNew = offer.status === OfferStatus.NEW
  const isAccepted = offer.status === OfferStatus.ACCEPTED
  const isDeclined =
    offer.status === OfferStatus.DECLINED_BY_CUSTOMER ||
    offer.status === OfferStatus.DECLINED_BY_VENDOR

  const handleOnChangeDeclineModal =
    (state: boolean): DeclineOfferModalProps['onClose'] =>
    (event) => {
      event.preventDefault()
      event.stopPropagation()

      changeOpenDecline(state)
    }

  const handleOnChangeDeleteModal =
    (state: boolean): DeleteOfferModalProps['onClose'] =>
    (event) => {
      event.preventDefault()
      event.stopPropagation()

      changeOpenDelete(state)
    }

  const handleOnOpenDeclineModal = () => {
    changeOpenDecline(true)
  }
  const handleOnOpenDeleteModal = () => {
    changeOpenDelete(true)
  }

  const handleOnAcceptOffer = () => {
    open({
      text: 'Success! the offer was accepted the Item was moved to the Cart',
      tx: 'offerUpdate.status.accepted.message'
    })

    const variables: OfferUpdateVariables = {
      id,
      input: {
        status: OfferStatus.ACCEPTED
      }
    }
    if (onChangeStatus) {
      onChangeStatus(variables)
    }
    if (onUpdateOfferTotalCount && isNew) {
      onUpdateOfferTotalCount(-1)
    }

    if (onAddOfferId) {
      onAddOfferId(offer.id)
    }
  }

  const handleOnReactivate = () => {
    const variables: OfferUpdateVariables = {
      id,
      input: {
        status: OfferStatus.NEW
      }
    }
    if (onUpdateOfferTotalCount) {
      onUpdateOfferTotalCount(+1)
    }

    if (onChangeStatus) {
      onChangeStatus(variables)
    }
  }

  const handleOnDecline = () => {
    const variables: OfferUpdateVariables = {
      id,
      input: {
        status: OfferStatus.DECLINED_BY_CUSTOMER
      }
    }
    if (onUpdateOfferTotalCount && isNew) {
      onUpdateOfferTotalCount(-1)
    }

    if (onChangeStatus) {
      onChangeStatus(variables)
    }
    if (onRemoveOfferId && isAccepted) {
      onRemoveOfferId(offer.id)
    }

    changeOpenDecline(false)
  }

  const handleOnDelete = () => {
    const deleteStatus =
      offer.status === OfferStatus.DECLINED_BY_VENDOR
        ? OfferStatus.DECLINED
        : OfferStatus.DELETED_BY_CUSTOMER
    const variables: OfferUpdateVariables = {
      id,
      input: {
        status: deleteStatus
      }
    }

    if (onChangeStatus) {
      onChangeStatus(variables)
    }

    changeOpenDelete(false)
  }

  const HeaderClose = (
    <OfferListItemHeader
      {...offer}
      imgUrl={product?.thumbnail2x.url}
      currency={currency}
      isDeclined={isDeclined}
      onAcceptOffer={handleOnAcceptOffer}
      onReactivate={handleOnReactivate}
    />
  )
  const Header = (
    <OfferListItemHeader
      {...offer}
      currency={currency}
      isDeclined={isDeclined}
      onAcceptOffer={handleOnAcceptOffer}
      onReactivate={handleOnReactivate}
    />
  )
  const HeaderMobile = (
    <OfferListItemHeaderMobile
      {...offer}
      currency={currency}
      isDeclined={isDeclined}
      onAcceptOffer={handleOnAcceptOffer}
      onReactivate={handleOnReactivate}
    />
  )

  return (
    <Column fullWidth className={classes.container}>
      <MenuSlider
        className={classes.containerSlider}
        titleClassName={classes.titleSlider}
        header={
          <ResponsiveMediaView
            bigScreen={Header}
            medium={Header}
            tablet={Header}
            mobile={HeaderMobile}
          />
        }
        headerClose={
          <ResponsiveMediaView
            bigScreen={HeaderClose}
            medium={HeaderClose}
            tablet={HeaderClose}
            mobile={HeaderMobile}
          />
        }
      >
        <OfferListItemLine
          {...offer}
          currency={currency}
          isDeclined={isDeclined}
          onOpenDeclineModal={handleOnOpenDeclineModal}
          onOpenDeleteModal={handleOnOpenDeleteModal}
          onContactVendor={onContactVendor}
        />
        {offer.items.map((item, index) => {
          const initialIndex = index + 1
          const itemNumber = zeroPad(initialIndex, 2)

          return (
            <OfferListItemVariantLine
              key={`offerLine_${item.id}_${index}`}
              {...offer}
              currency={currency}
              count={itemNumber}
              item={item}
              isDeclined={isDeclined}
              onOpenDeclineModal={handleOnOpenDeclineModal}
              onOpenDeleteModal={handleOnOpenDeleteModal}
              onContactVendor={onContactVendor}
            />
          )
        })}
        <DeclineOfferModal
          open={openDecline}
          description="Are you sure you want to decline this Offer?"
          descriptionTx="decline.offer.description"
          onClose={handleOnChangeDeclineModal(false)}
          onSubmit={handleOnDecline}
        />
        <DeleteOfferModal
          open={openDelete}
          description="Are you sure you want to delete this Offer?"
          descriptionTx="delete.offer.description"
          onClose={handleOnChangeDeleteModal(false)}
          onSubmit={handleOnDelete}
        />
      </MenuSlider>
    </Column>
  )
}
